import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getSupabase } from "@/lib/hooks/api/utils";
import type {
  FilterBasedBulkActionParams,
  BulkActionProgressItem,
  BulkActionProgress,
} from "@/lib/hooks/api/shared/bulk-filter-types";
import type { ContactFilterCriteria } from "./contacts-types";
import { RateLimitedBulkActionExecutor, getFilteredContactIds } from "./contacts-bulk-filter-utils";

export function useBulkUpdateContactStatusByFilter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      filterCriteria,
      status,
      batchSize = 50,
      delayMs = 1000,
      maxConcurrent = 3,
      onProgress,
    }: FilterBasedBulkActionParams<ContactFilterCriteria> & { status: string }) => {
      void batchSize;
      const contacts = await getFilteredContactIds(filterCriteria);

      if (contacts.length === 0) {
        throw new Error("No contacts found matching the current filters");
      }

      const progressItems: BulkActionProgressItem[] = contacts.map((contact) => ({
        id: contact.id,
        name: contact.name,
        status: "pending",
        retryCount: 0,
      }));

      const executor = new RateLimitedBulkActionExecutor(maxConcurrent, delayMs);

      let completedCount = 0;
      let errorCount = 0;

      const updateProgress = () => {
        const progress: BulkActionProgress = {
          items: progressItems,
          totalCount: contacts.length,
          completedCount,
          errorCount,
          isComplete: completedCount + errorCount === contacts.length,
        };
        onProgress?.(progress);
      };

      await executor.executeBulkAction(
        contacts,
        async (contact: { id: string; name: string }, index: number) => {
          const progressItem = progressItems[index];
          progressItem.status = "processing";
          updateProgress();

          try {
            const supabase = getSupabase();
            const { error } = await supabase
              .from("contacts")
              .update({ status, updated_at: new Date().toISOString() })
              .eq("id", contact.id);

            if (error) throw new Error(error.message);

            progressItem.status = "success";
            completedCount++;
          } catch (error) {
            progressItem.status = "error";
            progressItem.error = error instanceof Error ? error.message : "Unknown error";
            errorCount++;
          }

          updateProgress();
        },
        () => updateProgress()
      );

      return {
        totalProcessed: contacts.length,
        successful: completedCount,
        failed: errorCount,
        contacts: contacts.map((c) => c.id),
      };
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
      void queryClient.invalidateQueries({ queryKey: ["contacts-paginated"] });

      if (result.failed === 0) {
        toast.success(`Successfully updated ${result.successful} contacts`);
      } else {
        toast.warning(`Updated ${result.successful} contacts, ${result.failed} failed`);
      }
    },
    onError: (error) => {
      toast.error("Failed to update contact status");
      console.error("Error bulk updating contact status by filter:", error);
    },
  });
}

export function useBulkDeleteContactsByFilter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      filterCriteria,
      batchSize = 50,
      delayMs = 1000,
      maxConcurrent = 3,
      onProgress,
    }: FilterBasedBulkActionParams<ContactFilterCriteria>) => {
      void batchSize;
      const contacts = await getFilteredContactIds(filterCriteria);

      if (contacts.length === 0) {
        throw new Error("No contacts found matching the current filters");
      }

      const progressItems: BulkActionProgressItem[] = contacts.map((contact) => ({
        id: contact.id,
        name: contact.name,
        status: "pending",
        retryCount: 0,
      }));

      const executor = new RateLimitedBulkActionExecutor(maxConcurrent, delayMs);

      let completedCount = 0;
      let errorCount = 0;

      const updateProgress = () => {
        const progress: BulkActionProgress = {
          items: progressItems,
          totalCount: contacts.length,
          completedCount,
          errorCount,
          isComplete: completedCount + errorCount === contacts.length,
        };
        onProgress?.(progress);
      };

      await executor.executeBulkAction(
        contacts,
        async (contact: { id: string; name: string }, index: number) => {
          const progressItem = progressItems[index];
          progressItem.status = "processing";
          updateProgress();

          try {
            const supabase = getSupabase();
            const { error } = await supabase.from("contacts").delete().eq("id", contact.id);

            if (error) throw new Error(error.message);

            progressItem.status = "success";
            completedCount++;
          } catch (error) {
            progressItem.status = "error";
            progressItem.error = error instanceof Error ? error.message : "Unknown error";
            errorCount++;
          }

          updateProgress();
        }
      );

      return {
        totalProcessed: contacts.length,
        successful: completedCount,
        failed: errorCount,
        contacts: contacts.map((c) => c.id),
      };
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
      void queryClient.invalidateQueries({ queryKey: ["contacts-paginated"] });

      if (result.failed === 0) {
        toast.success(`Successfully deleted ${result.successful} contacts`);
      } else {
        toast.warning(`Deleted ${result.successful} contacts, ${result.failed} failed`);
      }
    },
    onError: (error) => {
      toast.error("Failed to delete contacts");
      console.error("Error bulk deleting contacts by filter:", error);
    },
  });
}

export function useBulkArchiveContactsByFilter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      filterCriteria,
      batchSize = 50,
      delayMs = 1000,
      maxConcurrent = 3,
      onProgress,
    }: FilterBasedBulkActionParams<ContactFilterCriteria>) => {
      void batchSize;
      const contacts = await getFilteredContactIds(filterCriteria);

      if (contacts.length === 0) {
        throw new Error("No contacts found matching the current filters");
      }

      const progressItems: BulkActionProgressItem[] = contacts.map((contact) => ({
        id: contact.id,
        name: contact.name,
        status: "pending",
        retryCount: 0,
      }));

      const executor = new RateLimitedBulkActionExecutor(maxConcurrent, delayMs);

      let completedCount = 0;
      let errorCount = 0;

      const updateProgress = () => {
        const progress: BulkActionProgress = {
          items: progressItems,
          totalCount: contacts.length,
          completedCount,
          errorCount,
          isComplete: completedCount + errorCount === contacts.length,
        };
        onProgress?.(progress);
      };

      await executor.executeBulkAction(
        contacts,
        async (contact: { id: string; name: string }, index: number) => {
          const progressItem = progressItems[index];
          progressItem.status = "processing";
          updateProgress();

          try {
            const supabase = getSupabase();
            const { error } = await supabase
              .from("contacts")
              .update({ is_active: false, updated_at: new Date().toISOString() })
              .eq("id", contact.id);

            if (error) throw new Error(error.message);

            progressItem.status = "success";
            completedCount++;
          } catch (error) {
            progressItem.status = "error";
            progressItem.error = error instanceof Error ? error.message : "Unknown error";
            errorCount++;
          }

          updateProgress();
        }
      );

      return {
        totalProcessed: contacts.length,
        successful: completedCount,
        failed: errorCount,
        contacts: contacts.map((c) => c.id),
      };
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
      void queryClient.invalidateQueries({ queryKey: ["contacts-paginated"] });

      if (result.failed === 0) {
        toast.success(`Successfully archived ${result.successful} contacts`);
      } else {
        toast.warning(`Archived ${result.successful} contacts, ${result.failed} failed`);
      }
    },
    onError: (error) => {
      toast.error("Failed to archive contacts");
      console.error("Error bulk archiving contacts by filter:", error);
    },
  });
}
